import { CloseOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import TooltipButton from './../../../../cms/src/components/TooltipButton';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Input, Typography } from 'antd';
import useFind from './useFind';

const ReplaceSvg = React.memo(() => (
  <svg viewBox="64 64 896 896" fill="currentColor">
    <path d="M227.312 682.685a348.587 348.587 0 0 0 566.937-12.525l71.073 71.073a432.075 432.075 0 0 1-703.405 15.548l-65.829 65.829v-206.35h206.35l-75.126 66.425z" />
    <path d="M796.688 341.315a348.587 348.587 0 0 0-566.937 12.525l-71.073-71.073a432.075 432.075 0 0 1 703.405-15.548l65.829-65.829v206.35h-206.35l75.126-66.425z" />
  </svg>
));

// Custom SVG for Replace All
const ReplaceAllSvg = React.memo(() => (
  <svg viewBox="64 64 896 896" fill="currentColor">
    <path d="M227.312 682.685a348.587 348.587 0 0 0 566.937-12.525l71.073 71.073a432.075 432.075 0 0 1-703.405 15.548l-65.829 65.829v-206.35h206.35l-75.126 66.425z" />
    <path d="M796.688 341.315a348.587 348.587 0 0 0-566.937 12.525l-71.073-71.073a432.075 432.075 0 0 1 703.405-15.548l65.829-65.829v206.35h-206.35l75.126-66.425z" />
    <path d="M680 608h-120v120h-56V608H384v-56h120V432h56v120h120z" />
  </svg>
));

const CaseSensitiveButton = React.memo(({ caseSensitive, onChange }) => {
  return (
    <TooltipButton
      title="Case sensitive"
      onClick={onChange}
      className={`find-option-action case-sensitive-btn ${caseSensitive ? 'active' : ''}`}
      icon={
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" className="find-option-action-icon">
          <path
            fill="currentColor"
            d="M8.854 11.702h-1l-.816-2.159H3.772l-.768 2.16H2L4.954 4h.935zm-2.111-2.97L5.534 5.45a3 3 0 0 1-.118-.515h-.021q-.054.327-.124.515L4.073 8.732zm7.013 2.97h-.88v-.86h-.022q-.574.99-1.692.99q-.822 0-1.29-.436q-.46-.435-.461-1.155q0-1.54 1.815-1.794l1.65-.23q0-1.403-1.134-1.403q-.994 0-1.794.677V6.59q.81-.516 1.87-.516q1.938 0 1.938 2.052zm-.88-2.782l-1.327.183q-.614.086-.924.306q-.312.215-.312.768q0 .403.285.66q.29.253.768.253a1.41 1.41 0 0 0 1.08-.457q.43-.462.43-1.165z"
          />
        </svg>
      }
    />
  );
});

function CmsFindReplace({ isEditable }) {
  const {
    query,
    replacement,
    caseSensitive,
    setQuery,
    setReplacement,
    find,
    findNext,
    findPrev,
    stopFind,
    replace,
    replaceAll,
    toggleCaseSensitive,
    total,
  } = useFind();
  const [isOpen, setIsOpen] = useState(false);
  const findDomRef = useRef(null);
  const findInputRef = useRef(null);

  const onClose = useCallback(() => {
    setIsOpen(false);
    stopFind();
  }, [setIsOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'f') {
        e.preventDefault();
        setIsOpen(true);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, []);

  useEffect(() => {
    let dom_element = null;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      } else if (e.key === 'ArrowDown') {
        e.preventDefault();
        findNext();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        findPrev();
      }
    };
    if (isOpen) {
      dom_element = findDomRef.current;
      if (findInputRef.current) findInputRef.current.focus();
      if (dom_element) dom_element?.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      if (dom_element) {
        dom_element?.removeEventListener('keydown', handleKeyDown);
      }
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div ref={findDomRef} className="find-and-replace-wrapper">
      {/* Find Input */}
      <div className="find-option-wrapper">
        <div className={`find-option-input-wrapper ${total > 0 ? 'has-add-on' : ''}`} span={12}>
          <Input
            ref={findInputRef}
            className={`find-option-input`}
            placeholder="Find"
            value={query}
            onChange={(e) => setQuery(String(e.target.value))}
          />
        </div>
        <div className="find-option-actions">
          {!isEditable && (
            <CaseSensitiveButton
              caseSensitive={caseSensitive}
              onChange={() => {
                toggleCaseSensitive();
              }}
              className="find-option-action"
            />
          )}
          <TooltipButton
            title="Previous"
            className="find-option-action"
            icon={<ArrowUpOutlined className="find-option-action-icon" />}
            onClick={findPrev}
          />
          <TooltipButton
            title="Next"
            className="find-option-action"
            icon={<ArrowDownOutlined className="find-option-action-icon" />}
            onClick={findNext}
          />
          <TooltipButton
            title="Close"
            className="find-option-action"
            icon={<CloseOutlined className="find-option-action-icon" />}
            onClick={onClose}
          />
        </div>
      </div>

      {/* Find Input */}
      {isEditable && (
        <>
          <div className="replace-option-wrapper">
            <div className="replace-option-input-wrapper" span={12}>
              <Input
                className="replace-option-input"
                placeholder="Replace"
                value={replacement}
                onChange={(e) => setReplacement(String(e.target.value))}
              />
            </div>
            <div className="replace-option-actions">
              {isEditable && (
                <CaseSensitiveButton
                  caseSensitive={caseSensitive}
                  onChange={() => {
                    toggleCaseSensitive();
                  }}
                  className="replace-option-action"
                />
              )}
              <TooltipButton
                title="Replace"
                className="replace-option-action"
                icon={<ReplaceSvg className="replace-option-action-icon" />}
                onClick={replace}
              />
              <TooltipButton
                title="Replace all"
                className="replace-option-action"
                icon={<ReplaceAllSvg className="replace-option-action-icon" />}
                onClick={replaceAll}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default React.memo(CmsFindReplace);
